import { Button, ButtonGroup, Checkbox, Dialog, Paper, TableContainer } from "@mui/material"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { CompanyRoleResponsibility, loadCompanyRoleResponsibilities } from "./companyRoleReponsibilitiesApi"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { Localized, useLocalization } from "@fluent/react"
import { selectSettings } from "../../../settings/settingsSlice"
import { unwrapResult } from "@reduxjs/toolkit"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { LoadingContainer } from "../../../../app/LoadingContainer"
import { ErrorContainer } from "../../../../app/ErrorContainer"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import produce from "immer"
import { QuickSearchToolbar } from "../../../../app/QuickSearchToolbar"
import { TeamMemberRoleLabel } from "../../../documents/components/teamMembers/TeamMemberRoleLabel"
import { DocumentTeamMemberRole } from "../../../documents/components/teamMemberApi"
import { RestoreSystemRoleResponsibilityDialog } from "./RestoreSystemRoleResponsibilityDialog"
import { OverrideSystemRoleReponsibilitiesDialog } from "./OverrideSystemRoleReponsibilitiesDialog"

type DialogState =
    | { type: "none" }
    | { type: "override", roleResponsibility: CompanyRoleResponsibility | undefined }
    | { type: "restore", roleResponsibility: CompanyRoleResponsibility }

export const CompanyRoleReponsibilities = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch();
    const [companyRoleResponsibilities, setCompanyRoleResponsibilities] = useState<CompanyRoleResponsibility[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [dialog, setDialog] = useState<DialogState>({ type: "none" });

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadCompanyRoleResponsibilities())
            .then(unwrapResult)
            .then(setCompanyRoleResponsibilities)
            .catch((error) => { console.log(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false));
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
        const onSaveSuccess = useCallback((companyRoleResponsibility: CompanyRoleResponsibility) => {
            setter({ type: "none" })
            setCompanyRoleResponsibilities(
                produce((draft) => {
                    if (draft !== undefined) {
                        const existingIndex = draft.findIndex((group) => group.roleCode === companyRoleResponsibility.roleCode);

                        if (existingIndex !== -1) {
                            draft[existingIndex] = companyRoleResponsibility;
                        } else {
                            draft.push(companyRoleResponsibility);
                        }
                    }
                })
            )
        }, [setter, dispatch])

        const onCancel = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (state.type) {
            case "none":
                return null

            case "override":
                return <OverrideSystemRoleReponsibilitiesDialog
                    roleResponsibility={state.roleResponsibility}
                    onSaveSuccess={onSaveSuccess}
                    onCancel={onCancel}
                />
            case "restore":
                return <RestoreSystemRoleResponsibilityDialog
                    roleCode={state.roleResponsibility.roleCode}
                    onSaveSuccess={onSaveSuccess}
                    onCancel={onCancel}
                />
        }
    }

    const handleOverrideSystemRoleResponsibilityClick = useCallback((companyRoleResponsibility: CompanyRoleResponsibility) => {
        setDialog({
            type: "override",
            roleResponsibility: companyRoleResponsibility
        })
    }, [setDialog])

    const handleRestoreSystemRoleResponsibilityClick = useCallback((companyRoleResponsibility: CompanyRoleResponsibility) => {
        setDialog({
            type: "restore",
            roleResponsibility: companyRoleResponsibility
        })
    }, [setDialog])

    const columns: GridColDef<CompanyRoleResponsibility, any, any>[] = [
        {
            field: 'roleCode',
            headerName: l10n.getString("document-user-role", null, "_roleCode"),
            width: 300,
            renderCell: (params) => (
                <TeamMemberRoleLabel role={params.value as DocumentTeamMemberRole} />
            ),
        },
        {
            field: 'responsibilityPl',
            headerName: l10n.getString("role-responsibility-pl", null, "_responsibilityPl"),
            flex: 1,
        },
        {
            field: 'responsibilityEn',
            headerName: l10n.getString("role-responsibility-en", null, "_responsibilityEn"),
            flex: 1,
        },
        {
            field: 'isSystem',
            headerName: 'System',
            width: 120,
            renderCell: (params) => (
                <Checkbox checked={params.value as boolean} disabled />
            ),
            sortable: false,
            filterable: false,
        },
        {
            field: 'actions',
            type: 'actions',
            width: 250,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <Button
                            style={{ width: 100 }}
                            color="secondary"
                            variant="outlined"
                            onClick={() => handleOverrideSystemRoleResponsibilityClick(params.row)}
                        >
                            <Localized id="edit">
                                <span>_Edytuj</span>
                            </Localized>
                        </Button>
                        <Button
                            style={{ width: 100 }}
                            color="error"
                            variant="outlined"
                            onClick={() => handleRestoreSystemRoleResponsibilityClick(params.row)}
                        >
                            <Localized id="restore">
                                <span>_Przywróć</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]

    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 275px)' }}
                    rows={companyRoleResponsibilities}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'eventDate', sort: 'desc' }] }
                    }}
                    slots={{ toolbar: QuickSearchToolbar }}
                    getRowId={(row) => row.roleCode}

                />
            </TableContainer>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth="sm" keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </>
    )
}
